import PlusOutlined from '@ant-design/icons/PlusOutlined';
import {
  Box,
  Dropdown,
  type FilterDropdownProps,
  Pagination,
  TagFilter,
  Tooltip,
  useTheme,
} from '@zennolab_platform/webui-kit/dist/ui';
import {
  Alert,
  ButtonANTD,
  type ColumnTypeANTD,
  ModalANTD,
  Spin,
  TableANTD,
} from '@zennolab_platform/webui-kit/dist/ui/antdComponents';
import {
  IconCopy,
  IconPencil,
  IconPin,
  IconPinActive,
  IconPinOff,
  IconStop,
  IconThreeDotsVertical,
} from '@zennolab_platform/webui-kit/dist/ui/Icon';
import { useFormatMessage } from '@zennolab_platform/webui-sdk/dist/hooks';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { FormattedDate, useIntl } from 'react-intl';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Container } from 'typedi';

import { FINAL_STATUSES, LIVE_STATUSES, PAGE_SIZES, STATUSES, TYPES } from '../../constants/announcement';
import { AnnouncementListController } from '../../controllers/AnnouncementListController';
import { useAnnouncementLabels } from '../../hooks/useAnnouncementLabels';
import { useErrorText } from '../../hooks/useErrorText';
import { useMountInit } from '../../hooks/useMountInit';
import { AnnouncementListStore } from '../../stores/AnnouncementListStore';
import type { IAnnouncementEntity, TSortKey } from '../../types/announcement';
import { isPinnable, localizedTitle, statusSlug, typeSlug, visibleAnnouncements } from '../../utils/announcement';
import { describeAudience } from '../../utils/audience';
import { Page, PageHeader, PageTitle, Toolbar } from '../layout';
import { ErrorAlert, NoAccess } from '../screens';
import { ImportantTag, StatusTag, TariffTag, TypeTag } from '../tags';
import { ListPanel, Pager, PinMark, RowMenuButton, TableArea, TitleCell, TypeCell } from './styled';

type RowAction = 'edit' | 'copy' | 'pin' | 'withdraw';

const MenuLabel = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <Box d="flex" fd="row" gap="10px" alignItems="center">
    {icon}
    <span>{text}</span>
  </Box>
);

const splitParam = (value: string | null) => (value ? value.split(',').filter(Boolean) : []);

export const AnnouncementList = observer(function AnnouncementList() {
  const store = Container.get(AnnouncementListStore);
  const controller = Container.get(AnnouncementListController);
  const navigate = useNavigate();
  const { t } = useFormatMessage();
  const intl = useIntl();
  const theme = useTheme();
  const { statusLabel, typeLabel } = useAnnouncementLabels();
  const errorText = useErrorText();
  const open = useCallback(() => {
    void controller.open();
  }, [controller]);
  const ready = useMountInit(open);
  const [params, setParams] = useSearchParams();
  if (!ready) return null;

  // Read here, in the observer: the cell renderers below run inside antd's table, outside its tracking.
  const { pinning } = store;

  const statusFilter = splitParam(params.get('status'));
  const typeFilter = splitParam(params.get('type'));
  // Filters live in the URL so links and "back" keep them; replace avoids a history entry per click.
  const setFilter = (key: 'status' | 'type', values: string[]) => {
    const next = new URLSearchParams(params);
    if (values.length) next.set(key, values.join(','));
    else next.delete(key);
    setParams(next, { replace: true });
  };
  /** Column header filter as in pfm (TagFilter of webui-kit). */
  const columnFilter = (key: 'status' | 'type', options: { text: string; value: string }[], value: string[]) => ({
    filters: options,
    filteredValue: value,
    filterDropdown: (props: FilterDropdownProps) => (
      <TagFilter
        testIdPrefix={`AnnouncementList${key}Filter`}
        onConfirm={values => setFilter(key, values)}
        emptyContentText={t({
          id: 'announcements.filter.empty',
          defaultMessage: 'Нет доступных значений',
          description: 'Фильтр колонки: нет значений',
        })}
        searchPlaceholder={t({
          id: 'announcements.filter.search',
          defaultMessage: 'Поиск',
          description: 'Фильтр колонки: плейсхолдер поиска',
        })}
        resetText={t({
          id: 'announcements.filter.reset',
          defaultMessage: 'Сбросить',
          description: 'Фильтр колонки: кнопка сброса',
        })}
        okText={t({
          id: 'announcements.filter.ok',
          defaultMessage: 'Ок',
          description: 'Фильтр колонки: кнопка подтверждения',
        })}
        {...props}
      />
    ),
  });

  if (store.state.status === 'failed' && store.state.error.kind === 'denied') return <NoAccess />;

  const onAction = (a: IAnnouncementEntity, action: RowAction) => {
    if (action === 'edit') navigate(`/announcements/${a.id}`);
    if (action === 'copy') navigate(`/announcements/new?copy=${a.id}`);
    if (action === 'pin') void controller.togglePin(a);
    if (action === 'withdraw') store.setWithdrawing(a);
  };

  /** Edit (or open a final one), copy (not a version card: one card per version) and withdraw a live one. */
  const actionItems = (a: IAnnouncementEntity) => [
    {
      key: 'edit',
      label: (
        <MenuLabel
          icon={<IconPencil />}
          text={
            FINAL_STATUSES.includes(a.status)
              ? t({
                  id: 'announcements.list.view',
                  defaultMessage: 'Открыть',
                  description: 'Пункт меню строки списка для завершённого анонса',
                })
              : t({
                  id: 'announcements.list.edit',
                  defaultMessage: 'Изменить',
                  description: 'Пункт меню строки списка',
                })
          }
        />
      ),
    },
    ...(a.businessVersion === undefined
      ? [
          {
            key: 'copy',
            label: (
              <MenuLabel
                icon={<IconCopy />}
                text={t({
                  id: 'announcements.list.copy',
                  defaultMessage: 'Создать на основе',
                  description: 'Пункт меню строки списка',
                })}
              />
            ),
          },
        ]
      : []),
    ...(isPinnable(a)
      ? [
          {
            key: 'pin',
            disabled: pinning.includes(a.id),
            label: a.pinned ? (
              <MenuLabel
                icon={<IconPinOff />}
                text={t({
                  id: 'announcements.list.unpin',
                  defaultMessage: 'Открепить',
                  description: 'Пункт меню строки списка и подсказка к значку закрепления',
                })}
              />
            ) : (
              <MenuLabel
                icon={<IconPin />}
                text={t({
                  id: 'announcements.list.pin',
                  defaultMessage: 'Закрепить',
                  description: 'Пункт меню строки списка',
                })}
              />
            ),
          },
        ]
      : []),
    ...(LIVE_STATUSES.includes(a.status)
      ? [
          {
            key: 'withdraw',
            danger: true,
            label: (
              <MenuLabel
                icon={<IconStop />}
                text={t({
                  id: 'announcements.list.withdraw',
                  defaultMessage: 'Снять с публикации',
                  description: 'Пункт меню строки списка',
                })}
              />
            ),
          },
        ]
      : []),
  ];

  /** Sorting is ours, over the whole list, so pages follow it; antd only shows the arrows. */
  const sortable = (key: TSortKey) => ({ sorter: true, sortOrder: store.sort?.key === key ? store.sort.order : null });
  const columns: ColumnTypeANTD<IAnnouncementEntity>[] = [
    {
      key: 'pinned',
      title: '',
      width: 40,
      align: 'center',
      // A click on the pin unpins, as in the pfm profile table.
      render: (_, a) => {
        if (!a.pinned) return false;
        const pinLabel = isPinnable(a)
          ? t({
              id: 'announcements.list.unpin',
              defaultMessage: 'Открепить',
              description: 'Пункт меню строки списка и подсказка к значку закрепления',
            })
          : t({
              id: 'announcements.list.pinned',
              defaultMessage: 'Закреплено',
              description: 'Подсказка к значку закрепления, когда открепить нельзя',
            });
        return (
          // The pin sits at the left edge of the table: anchor the tooltip there so it is not clipped.
          <Tooltip title={pinLabel} placement="topLeft">
            <PinMark
              type="button"
              aria-label={pinLabel}
              disabled={!isPinnable(a) || pinning.includes(a.id)}
              onClick={() => void controller.togglePin(a)}
            >
              <IconPinActive color={theme.color.Blue100} />
            </PinMark>
          </Tooltip>
        );
      },
    },
    {
      key: 'title',
      title: t({ id: 'announcements.list.title', defaultMessage: 'Заголовок', description: 'Колонка списка анонсов' }),
      // At most 300px: a long title wraps, the other columns get the room.
      width: 300,
      ...sortable('title'),
      // The row menu sits at the end of the title, as in the pfm profile table; it shows on row hover.
      render: (_, a) => (
        <TitleCell>
          <Link to={`/announcements/${a.id}`}>{localizedTitle(a, intl.locale)}</Link>
          <Dropdown
            items={actionItems(a)}
            onClick={({ key }) => onAction(a, key as RowAction)}
            trigger={['click']}
            arrow
            placement="bottomRight"
          >
            <RowMenuButton
              aria-label={t({
                id: 'announcements.list.actions',
                defaultMessage: 'Действия',
                description: 'Подпись кнопки меню действий строки списка',
              })}
              type="text"
              size="small"
              icon={<IconThreeDotsVertical color={theme.color.Neutral70} />}
            />
          </Dropdown>
        </TitleCell>
      ),
    },
    {
      key: 'status',
      title: t({ id: 'announcements.list.status', defaultMessage: 'Статус', description: 'Колонка списка анонсов' }),
      render: (_, a) => <StatusTag status={a.status} />,
      ...sortable('status'),
      ...columnFilter(
        'status',
        STATUSES.map(s => ({ text: statusLabel(s), value: statusSlug(s) })),
        statusFilter,
      ),
    },
    {
      key: 'type',
      title: t({ id: 'announcements.list.type', defaultMessage: 'Тип', description: 'Колонка списка анонсов' }),
      render: (_, a) => (
        <TypeCell>
          <TypeTag type={a.type} />
          {a.important && <ImportantTag />}
        </TypeCell>
      ),
      ...sortable('type'),
      ...columnFilter(
        'type',
        TYPES.map(type => ({ text: typeLabel(type), value: typeSlug(type) })),
        typeFilter,
      ),
    },
    {
      key: 'updatedAt',
      title: t({
        id: 'announcements.list.updatedAt',
        defaultMessage: 'Изменён',
        description: 'Колонка списка анонсов',
      }),
      ...sortable('updatedAt'),
      render: (_, a) => (a.updatedAt ? <FormattedDate value={a.updatedAt} dateStyle="short" timeStyle="short" /> : ''),
    },
    {
      key: 'version',
      title: t({ id: 'announcements.list.version', defaultMessage: 'Версия', description: 'Колонка списка анонсов' }),
      ...sortable('version'),
      render: (_, a) => a.businessVersion ?? '',
    },
    {
      key: 'audience',
      // Same weight as the sortable headers, whose title antd wraps in this class.
      title: (
        <span className="ant-table-column-title">
          {t({ id: 'announcements.list.audience', defaultMessage: 'Аудитория', description: 'Колонка списка анонсов' })}
        </span>
      ),
      render: (_, a) => {
        const tariffs = a.audience?.tariffs ?? [];
        // Tariffs as tags in the UA2 colours, the rest of the audience as text.
        const rest = a.audience ? describeAudience(t, { ...a.audience, tariffs: [] }) : '';
        if (!tariffs.length && !rest) {
          return t({
            id: 'announcements.list.all',
            defaultMessage: 'Все',
            description: 'Аудитория анонса в списке: все пользователи',
          });
        }
        return (
          <Box d="flex" flexWrap="wrap" alignItems="center" gap="4px">
            {tariffs.map(code => (
              <TariffTag key={code} code={code} />
            ))}
            {rest && <span>{rest}</span>}
          </Box>
        );
      },
    },
  ];

  // Pinned announcements stay on top, as in the ZennoBrowser feed, under any column sort.
  const filtered =
    store.state.status === 'loaded'
      ? visibleAnnouncements(store.state.items, {
          statusFilter,
          typeFilter,
          sort: store.sort,
          typeLabel,
          locale: intl.locale,
        })
      : [];
  const current = Math.min(store.page, Math.max(1, Math.ceil(filtered.length / store.pageSize)));
  const rows = filtered.slice((current - 1) * store.pageSize, current * store.pageSize);
  const filtering = statusFilter.length > 0 || typeFilter.length > 0;

  const isCard = store.withdrawing?.businessVersion !== undefined;
  return (
    <Page>
      <ListPanel>
        <PageHeader>
          <PageTitle>
            {t({
              id: 'shell.nav.announcements',
              defaultMessage: 'Анонсы',
              description: 'Раздел админки в боковом меню и заголовок списка',
            })}
          </PageTitle>
          {store.state.status === 'loaded' && (
            <Toolbar>
              <ButtonANTD
                type="primary"
                icon={<PlusOutlined aria-hidden />}
                onClick={() => navigate('/announcements/new')}
              >
                {t({
                  id: 'announcements.list.new',
                  defaultMessage: 'Новый анонс',
                  description: 'Кнопка в шапке списка анонсов',
                })}
              </ButtonANTD>
              <ButtonANTD onClick={() => navigate('/announcements/version-card/new')}>
                {t({
                  id: 'announcements.list.newVersionCard',
                  defaultMessage: 'Новая карточка версии',
                  description: 'Кнопка в шапке списка анонсов',
                })}
              </ButtonANTD>
            </Toolbar>
          )}
        </PageHeader>
        {store.actionError && <Alert type="error" title={errorText(store.actionError)} />}
        {store.state.status === 'loading' && <Spin />}
        {store.state.status === 'failed' && (
          <ErrorAlert error={store.state.error} onRetry={() => void controller.retry()} />
        )}
        {store.state.status === 'loaded' && (
          <>
            <TableArea>
              <TableANTD<IAnnouncementEntity>
                rowKey="id"
                columns={columns}
                dataSource={rows}
                size="middle"
                pagination={false}
                showSorterTooltip={false}
                onChange={(_pagination, _filters, sorter, extra) => {
                  if (extra.action !== 'sort') return;
                  const s = Array.isArray(sorter) ? sorter[0] : sorter;
                  store.setSort(s?.order ? { key: s.columnKey as TSortKey, order: s.order } : undefined);
                }}
                locale={{
                  emptyText: filtering
                    ? t({
                        id: 'announcements.list.noMatches',
                        defaultMessage: 'Нет анонсов с такими фильтрами',
                        description: 'Пустой список при включённых фильтрах',
                      })
                    : t({
                        id: 'announcements.list.empty',
                        defaultMessage: 'Анонсов пока нет',
                        description: 'Пустой список анонсов',
                      }),
                }}
              />
            </TableArea>
            <Pager>
              <Pagination
                current={current}
                pageSize={store.pageSize}
                total={filtered.length}
                showSizeChanger
                pageSizeOptions={PAGE_SIZES}
                onChange={(next, size) => store.setPagination(next, size)}
              />
            </Pager>
          </>
        )}
      </ListPanel>
      <ModalANTD
        open={store.withdrawing !== null}
        title={
          isCard
            ? t({
                id: 'announcements.versionCard.withdrawConfirmTitle',
                defaultMessage: 'Снять карточку версии с публикации?',
                description: 'Заголовок подтверждения снятия карточки версии с публикации',
              })
            : t({
                id: 'announcements.form.withdrawConfirmTitle',
                defaultMessage: 'Снять анонс с публикации?',
                description: 'Заголовок подтверждения снятия анонса с публикации',
              })
        }
        okText={t({
          id: 'announcements.form.withdraw',
          defaultMessage: 'Снять с публикации',
          description: 'Кнопка снятия с публикации и подтверждения',
        })}
        okButtonProps={{ danger: true }}
        cancelText={t({ id: 'announcements.form.cancel', defaultMessage: 'Отмена', description: 'Кнопка отмены' })}
        onOk={() => void controller.confirmWithdraw()}
        onCancel={() => store.setWithdrawing(null)}
      >
        {isCard
          ? t({
              id: 'announcements.versionCard.withdrawConfirmText',
              defaultMessage: 'Карточка исчезнет из ленты всех пользователей. Вернуть её нельзя.',
              description: 'Текст подтверждения снятия карточки версии с публикации',
            })
          : t({
              id: 'announcements.form.withdrawConfirmText',
              defaultMessage: 'Анонс исчезнет из ленты всех пользователей. Вернуть его нельзя.',
              description: 'Текст подтверждения снятия анонса с публикации',
            })}
      </ModalANTD>
    </Page>
  );
});
